import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, InputAdornment, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { CommonTextField } from 'gemlux-common-components';
import { theme } from '../../../theme/theme';
import CustomButton from '../../V1/CustomButton/CustomButton';
import { userActions } from '../../../redux/user/userSlice';
import VisibilitySvg from '../../../assets/img/visibility.svg';
import VisibilityOffSvg from '../../../assets/img/visibility-off.svg';
import { RootState } from '../../../redux/store';
import CustomModal from '../../V1/CustomModal/CustomModal';
import AlertIcon from '../../../assets/img/alert-yellow.svg';

const validationSchema = yup.object().shape({
  password: yup.string().required('Current password is required!'),

  newPassword: yup
    .string()
    .min(8, 'Password should be at least 8 characters long')
    .max(30, 'Password should not contain more than 30 characters')
    .matches(/\d/, 'Password requires a number')
    .matches(/[a-z]/, 'Password requires a lowercase letter')
    .matches(/[A-Z]/, 'Password requires an uppercase letter')
    .matches(/[^\w]/, 'Password requires a special character')
    .test(
      'newPassword-not-equal-current',
      'New password cannot be the same as the current password',
      function newPasswordNotEqualCurrent(newPass) {
        return newPass !== this.parent.password;
      },
    )
    .required('New password is required!'),

  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords do not match!')
    .required('Confirm password is required!'),
});

interface PasswordFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  showPassword: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
  error: boolean | undefined;
  helperText: string | false | undefined;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  name,
  label,
  value,
  showPassword,
  setShowPassword,
  onChange,
  onBlur,
  error,
  helperText,
}) => (
  <Grid item xs={12} sm={12} display="flex" flexDirection="column" gap={0.5} width="100%">
    <Typography variant="textBase500" component="label" htmlFor={id}>
      {label}
    </Typography>
    <CommonTextField
      height={40}
      id={id}
      name={name}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      autoComplete="off"
      variant="outlined"
      FormHelperTextProps={{ style: { ...theme.typography.footnoteRegular2, marginLeft: 0 } }}
      errorTextColor="#DF0C3D"
      errorBorderColor="#DF0C3D"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
              onMouseLeave={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
              sx={{
                backgroundColor: '#E0EDFF',
                backdropFilter: 'blur(6px)',
                borderRadius: '8px',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: theme.palette.secondary.light,
                },
              }}
            >
              {showPassword ? (
                <Box component="img" src={VisibilityOffSvg} />
              ) : (
                <Box component="img" src={VisibilitySvg} />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  </Grid>
);

export default function ChangePassword(): React.ReactElement {
  const dispatch = useDispatch();
  const componentStatus = useSelector((state: RootState) => state.user.componentStatus);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [generalError, setGeneralError] = useState<string>('');

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: () => {
      // The submission is now handled via handleUpdatePassword
    },
  });

  const handleUpdatePassword = () => {
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        dispatch(userActions.checkPasswordAsync({ password: formik.values.password }));
      }
    });
  };

  const handleConfirmUpdate = () => {
    dispatch(
      userActions.changePasswordAsync({
        password: formik.values.password,
        newPassword: formik.values.newPassword,
      }),
    );
    setIsConfirmationOpen(false);
  };

  useEffect(() => {
    if (componentStatus.successModalOpen) {
      dispatch(userActions.clearComponentStatus(['successModalOpen']));
      if (generalError) {
        setGeneralError('');
      }
      formik.resetForm();
    }
    if (componentStatus.isCurrentPasswordIncorrect) {
      if (generalError) {
        setGeneralError('');
      }
      formik.setFieldError('password', 'The current password is incorrect');
      dispatch(userActions.clearComponentStatus(['isCurrentPasswordIncorrect']));
    }

    if (componentStatus.checkPasswordError) {
      setGeneralError(typeof componentStatus.checkPasswordError === 'string' ? componentStatus.checkPasswordError : '');
      dispatch(userActions.clearComponentStatus(['checkPasswordError']));
    }

    if (componentStatus.isPasswordCorrect) {
      setIsConfirmationOpen(true);
      dispatch(userActions.clearComponentStatus(['isPasswordCorrect']));
    }

    if (componentStatus.errorFields) {
      if (generalError) {
        setGeneralError('');
      }
      dispatch(userActions.clearComponentStatus(['errorFields']));
    } else if (componentStatus.error) {
      const errorMessage =
        typeof componentStatus.error === 'string' ? componentStatus.error : 'An unexpected error occurred.';
      setGeneralError(errorMessage);
      dispatch(userActions.clearComponentStatus(['error']));
    }
  }, [componentStatus, dispatch, formik, generalError]);

  const isUpdateDisabled = !formik.isValid || Boolean(componentStatus.isSubmitting);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isUpdateDisabled) {
        formik.submitForm().then(() => {
          handleUpdatePassword();
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUpdateDisabled, formik]);

  return (
    <Grid
      container
      spacing={2}
      marginTop={1}
      marginLeft={1}
      paddingRight="2%"
      sx={{
        border: `1px solid ${theme.palette.shades.grayBorder}`,
        borderRadius: '8px',
        display: 'flex',
        width: '35%',
        flexDirection: 'column',
        background: 'var(--Background-bg-base, #FFF)',
        boxShadow: '0px 1px 2px 0px rgba(128, 128, 128, 0.12)',
      }}
    >
      <PasswordField
        id="password"
        name="password"
        label="Current Password"
        value={formik.values.password}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(formik.touched.password && formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
      />

      <PasswordField
        id="newPassword"
        name="newPassword"
        label="New Password"
        value={formik.values.newPassword}
        showPassword={showNewPassword}
        setShowPassword={setShowNewPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(formik.touched.newPassword && formik.errors.newPassword)}
        helperText={formik.touched.newPassword && formik.errors.newPassword}
      />

      <PasswordField
        id="confirmPassword"
        name="confirmPassword"
        label="Confirm Password"
        value={formik.values.confirmPassword}
        showPassword={showConfirmPassword}
        setShowPassword={setShowConfirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
        helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
      />

      {generalError && (
        <Grid item xs={12}>
          <Typography color="error" variant="footnoteRegular2">
            {generalError}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} display="flex" justifyContent="flex-end" paddingBottom={3}>
        <Box>
          <CustomButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpdatePassword}
            disabled={isUpdateDisabled}
          >
            {componentStatus.isSubmitting ? 'Checking...' : 'Update'}
          </CustomButton>
        </Box>
      </Grid>

      <CustomModal
        modal
        open={isConfirmationOpen}
        handleClose={() => setIsConfirmationOpen(false)}
        Icon={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '32px',
              background: 'var(--Warning-warning-200, #FFED9E)',
            }}
          >
            <Box
              component="img"
              src={AlertIcon}
              alt="Alert Icon"
              sx={{
                width: '40px',
                height: '40px',
              }}
            />
          </Box>
        }
        title="Updating Password"
        subTitle="Are you sure you want to update your password?"
      >
        <Box display="flex" flexDirection="column" gap="8px" justifyContent="end" alignItems="center" mt={1}>
          <CustomButton
            variant="contained"
            data-testid="confirm-update-button"
            sx={{
              width: '100%',
              backgroundColor: theme.palette.shades.primaryBrightDark,
              marginTop: '10px',
            }}
            onClick={handleConfirmUpdate}
            disabled={Boolean(componentStatus.isSubmitting)}
          >
            {componentStatus.isSubmitting ? 'Updating...' : 'Yes, Update'}
          </CustomButton>
          <CustomButton
            variant="outlined"
            data-testid="cancel-button"
            sx={{
              width: '100%',
              color: theme.palette.common.black,
              border: `1px solid ${theme.palette.shades.grayBorder}`,
            }}
            onClick={() => setIsConfirmationOpen(false)}
          >
            Cancel
          </CustomButton>
        </Box>
      </CustomModal>
    </Grid>
  );
}
